import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/config';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Users, 
  Search, 
  Store, 
  Mail, 
  Phone, 
  Calendar,
  CheckCircle,
  Clock,
  XCircle,
  ChevronDown,
  ChevronUp,
  Download,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const VendorOverview = () => {
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedVendor, setExpandedVendor] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    filterVendors();
  }, [vendors, searchQuery]);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/vendors`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setVendors(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      toast.error('Failed to load vendors');
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  const filterVendors = () => {
    if (!searchQuery) {
      setFilteredVendors(vendors);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = vendors.filter(vendor =>
      (vendor.email && vendor.email.toLowerCase().includes(query)) ||
      (vendor.business_name && vendor.business_name.toLowerCase().includes(query)) ||
      (vendor.contact_phone && vendor.contact_phone.includes(query)) ||
      (vendor.stall_names && vendor.stall_names.some(name => name.toLowerCase().includes(query)))
    );
    setFilteredVendors(filtered);
  };

  const getVerificationBadge = (status) => {
    switch (status) {
      case 'verified':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Verified</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">Unverified</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const handleExportVendors = () => {
    if (filteredVendors.length === 0) {
      toast.error('No vendors to export');
      return;
    }

    const headers = ['Email', 'Business Name', 'Contact Phone', 'Contact Email', 'Verification Status', 'Stalls Created', 'Stalls Claimed', 'Stall Names', 'Joined Date'];
    const csvRows = [headers.join(',')];

    filteredVendors.forEach(vendor => {
      const row = [
        `"${vendor.email || ''}"`,
        `"${vendor.business_name || ''}"`,
        `"${vendor.contact_phone || ''}"`,
        `"${vendor.contact_email || ''}"`,
        `"${vendor.verification_status || 'unverified'}"`,
        vendor.stalls_created || 0,
        vendor.stalls_claimed || 0,
        `"${(vendor.stall_names || []).join('; ')}"`,
        `"${formatDate(vendor.created_at)}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `vendors_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Exported ${filteredVendors.length} vendors`);
  };

  const toggleExpand = (vendorId) => {
    setExpandedVendor(expandedVendor === vendorId ? null : vendorId);
  };

  const openDeleteDialog = (vendor, e) => {
    e.stopPropagation(); // Prevent card expansion
    setVendorToDelete(vendor);
    setDeleteDialogOpen(true);
  };

  const handleDeleteVendor = async () => {
    if (!vendorToDelete) return;
    
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/admin/vendors/${vendorToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      toast.success(response.data.message || 'Vendor deleted successfully');
      
      // Remove vendor from local state
      setVendors(prev => prev.filter(v => v.id !== vendorToDelete.id));
      setDeleteDialogOpen(false);
      setVendorToDelete(null);
    } catch (error) {
      console.error('Error deleting vendor:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete vendor');
    } finally {
      setIsDeleting(false);
    }
  };

  // Calculate statistics
  const stats = {
    total: vendors.length,
    verified: vendors.filter(v => v.verification_status === 'verified').length,
    withStalls: vendors.filter(v => v.stalls_created > 0).length,
    totalStalls: vendors.reduce((sum, v) => sum + (v.stalls_created || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendor-overview">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Total Vendors</p>
                <p className="text-2xl font-bold text-blue-800" data-testid="total-vendors">{stats.total}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Verified</p>
                <p className="text-2xl font-bold text-green-800" data-testid="verified-vendors">{stats.verified}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">With Stalls</p>
                <p className="text-2xl font-bold text-purple-800" data-testid="vendors-with-stalls">{stats.withStalls}</p>
              </div>
              <Store className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600 font-medium">Total Stalls</p>
                <p className="text-2xl font-bold text-orange-800" data-testid="total-stalls">{stats.totalStalls}</p>
              </div>
              <Store className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Export */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="relative flex-1 w-full md:max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            placeholder="Search by email, business name, phone, or stall..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 border-gray-300"
            data-testid="vendor-search-input"
          />
        </div>
        <Button
          onClick={handleExportVendors}
          variant="outline"
          className="border-green-500 text-green-700 hover:bg-green-50"
          data-testid="export-vendors-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Results Count */}
      <p className="text-sm text-gray-600">
        Showing {filteredVendors.length} of {vendors.length} vendors
      </p>

      {/* Vendor List */}
      <div className="space-y-3">
        {filteredVendors.length === 0 ? (
          <Card className="p-8 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchQuery ? 'No vendors match your search' : 'No vendors registered yet'}
            </p>
          </Card>
        ) : (
          filteredVendors.map((vendor) => (
            <Card 
              key={vendor.id} 
              className="hover:shadow-md transition-shadow"
              data-testid={`vendor-card-${vendor.id}`}
            >
              <CardContent className="p-4">
                {/* Main Row - Always Visible */}
                <div 
                  className="flex flex-col md:flex-row md:items-center justify-between gap-4 cursor-pointer"
                  onClick={() => toggleExpand(vendor.id)}
                >
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-semibold text-lg text-gray-800" data-testid={`vendor-name-${vendor.id}`}>
                        {vendor.business_name || vendor.email.split('@')[0]}
                      </h3>
                      {getVerificationBadge(vendor.verification_status)}
                      {!vendor.is_active && (
                        <Badge className="bg-red-100 text-red-800">Inactive</Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {vendor.email}
                      </span>
                      {vendor.contact_phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-4 h-4" />
                          {vendor.contact_phone}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Joined {formatDate(vendor.created_at)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Stall Stats */}
                    <div className="flex gap-4 text-center">
                      <div className="px-3 py-1 bg-blue-50 rounded-lg">
                        <p className="text-xs text-blue-600">Created</p>
                        <p className="text-lg font-bold text-blue-800" data-testid={`stalls-created-${vendor.id}`}>
                          {vendor.stalls_created}
                        </p>
                      </div>
                      <div className="px-3 py-1 bg-green-50 rounded-lg">
                        <p className="text-xs text-green-600">Claimed</p>
                        <p className="text-lg font-bold text-green-800" data-testid={`stalls-claimed-${vendor.id}`}>
                          {vendor.stalls_claimed}
                        </p>
                      </div>
                    </div>
                    
                    <Button variant="ghost" size="sm" className="p-2">
                      {expandedVendor === vendor.id ? (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedVendor === vendor.id && (
                  <div className="mt-4 pt-4 border-t border-gray-100" data-testid={`vendor-expanded-${vendor.id}`}>
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Contact Details */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Contact Details</h4>
                        <div className="space-y-1 text-sm">
                          <p><span className="text-gray-500">Email:</span> {vendor.email}</p>
                          <p><span className="text-gray-500">Contact Email:</span> {vendor.contact_email || 'Not provided'}</p>
                          <p><span className="text-gray-500">Phone:</span> {vendor.contact_phone || 'Not provided'}</p>
                          <p><span className="text-gray-500">Business:</span> {vendor.business_name || 'Not set'}</p>
                        </div>
                      </div>

                      {/* Stall List */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">
                          Stalls ({vendor.stall_names?.length || 0})
                        </h4>
                        {vendor.stall_names && vendor.stall_names.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {vendor.stall_names.map((name, index) => (
                              <Badge 
                                key={index} 
                                variant="outline" 
                                className="bg-gray-50"
                              >
                                <Store className="w-3 h-3 mr-1" />
                                {name}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No stalls created yet</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Delete Vendor Button */}
                    <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => openDeleteDialog(vendor, e)}
                        className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                        data-testid={`delete-vendor-btn-${vendor.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remove Vendor
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-vendor-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Remove Vendor
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Are you sure you want to remove <strong>{vendorToDelete?.business_name || vendorToDelete?.email}</strong>?
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                <p className="text-sm text-amber-800">
                  <strong>What happens:</strong>
                </p>
                <ul className="text-sm text-amber-700 list-disc list-inside mt-1">
                  <li>Vendor account will be permanently deleted</li>
                  <li>Their stalls will be marked as "unclaimed" (not deleted)</li>
                  <li>Their support tickets will be removed</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting} data-testid="cancel-delete-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteVendor}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {isDeleting ? 'Removing...' : 'Remove Vendor'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VendorOverview;
